import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { useUpdateExamConfig } from "@/apis/exam_configs/updateExamConfig";
import type { IUpdateExamConfigRequest } from "@/apis/exam_configs/updateExamConfig";
import { useQueryGetExamConfig } from "@/apis/exam_configs/getExamConfig";
import ExamConfigForm from "./components/ExamConfigForm";
import type { ICreateExamConfigRequest } from "@/apis/exam_configs/createExamConfig";

const ExamConfigEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  if (!id) {
    navigate("/exam_configs");
    return null;
  }

  // 시험 설정 정보 조회
  const {
    data: examConfig,
    isLoading: isLoadingExamConfig,
    error: examConfigError,
  } = useQueryGetExamConfig(id);

  // 수정 API 훅
  const { mutate: updateExamConfig, isPending: isUpdating } =
    useUpdateExamConfig(id);
  const [serverError, setServerError] = useState<string | undefined>();

  // 폼 제출 핸들러
  const handleSubmit = (formData: ICreateExamConfigRequest) => {
    if (!examConfig) return;

    // 업데이트 데이터 준비 (ID는 제외하고 subjects만 전송)
    const updateData: IUpdateExamConfigRequest = {
      subjects: formData.subjects,
    };

    // API 호출
    updateExamConfig(updateData, {
      onSuccess: () => {
        // 성공 시 상세 페이지로 이동
        navigate(`/exam_configs/${id}`);
      },
      onError: (error: any) => {
        // 서버 오류 처리
        setServerError(error?.message || "Failed to update exam configuration");
      },
    });
  };

  // 상세 페이지로 돌아가기
  const handleCancel = () => {
    navigate(`/exam_configs/${id}`);
  };

  // 로딩 중
  if (isLoadingExamConfig) {
    return (
      <PageContainer>
        <div className="p-8 text-center">
          <Text>Loading exam configuration...</Text>
        </div>
      </PageContainer>
    );
  }

  // 오류 발생
  if (examConfigError || !examConfig) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Failed to load exam configuration. Please try again.</Text>
          <Button onClick={() => navigate("/exam_configs")} className="mt-4">
            Back to List
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button plain onClick={handleCancel}>
            <ArrowLeftIcon className="h-5 w-5" />
            Back
          </Button>

          <div>
            <Heading>Edit Exam Configuration: {examConfig.id}</Heading>
            <Text className="text-zinc-500">
              Update the subjects, durations, and calculator permissions below.
            </Text>
          </div>
        </div>

        {/* 시험 설정 수정 폼 */}
        <div className="p-8 border border-gray-200 rounded-md bg-white">
          <ExamConfigForm
            initialData={examConfig}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isUpdating}
            serverError={serverError}
            isEditMode={true}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default ExamConfigEditPage;
